import NetInfo from '@react-native-community/netinfo';
import Navigation from '@vfgroup-oneplatform/foundation/Utils/Navigation';
import axios from 'axios';
import { isEqual as _isEqual, isEmpty as _isEmpty } from 'lodash';

import Status from '../Utils/AuthStatusEnum';

import { InvalidArgumentsException, SeamlessAmbiguityException } from './LoginManager.Exception';

class LoginManager {
  constructor(config) {
    const __config = Object.assign(
      {},
      {
        seamless: null,
        soft: null,
        upfront: null,
        register: null,
        verifyCode: null,
        savedAccounts: null,
        renderLoginIconView: null,
        renderVerifyLoginIconView: null,
      },
      config,
    );

    const {
      seamless,
      soft,
      upfront,
      register,
      verifyCode,
      savedAccounts,
      renderLoginIconView,
      renderVerifyLoginIconView,
    } = __config;
    this.__loginSeamless = seamless;
    this.__loginSoft = soft;
    this.__loginUpfront = upfront;
    this.__register = register;
    this.__verifyCode = verifyCode;
    this.__savedAccounts = savedAccounts;
    this.hasUpfrontLogin = true;
    this.renderLoginIconView = renderLoginIconView;
    this.renderVerifyLoginIconView = renderVerifyLoginIconView;
  }

  init = async () => {
    this.hasUpfrontLogin = !!this.__loginUpfront;
    if (this.__loginSeamless) {
      const result = await this.handleSeamlessLogin(
        this.__loginSoft || this.__savedAccounts || this.__loginUpfront,
      );
      return result;
    } else if (!this.__loginSeamless && !this.__loginSoft && !this.__loginUpfront) {
      throw new InvalidArgumentsException('Empty object Passed to LoginManager');
    } else {
      this.__loginSoft
        ? this.__loginSoft.onLoginStart()
        : this.__savedAccounts && (await this.hasSavedAccountList())
          ? this.__savedAccounts.onLoginStart()
          : this.__loginUpfront.onLoginStart();
      return this.__loginSoft
        ? (...args) => {
          this.softLogin(...args);
        }
        : this.__savedAccounts && (await this.hasSavedAccountList())
          ? (...args) => {
            this.savedAccountsLogin(...args);
          }
          : (...args) => {
            this.upfrontLogin(...args);
          };
    }
  };

  hasSavedAccountList = async () => {
    try {
      const accountList = await this.__savedAccounts.getSavedAccountsList();
      return !_isEmpty(accountList);
    } catch (error) {
      return false;
    }
  };

  handleSeamlessLogin = async next => {
    const nextLogin = _isEqual(next, this.__loginSoft)
      ? () => {
        this.softLogin();
      }
      : _isEqual(next, this.__savedAccounts) && (await this.hasSavedAccountList())
        ? () => {
          this.savedAccountsLogin();
        }
        : () => {
          this.upfrontLogin();
        };
    try {
      const netState = await NetInfo.fetch();
      if (netState.isConnected) {
        switch (netState.type) {
          case 'cellular':
            const result = await this.__loginSeamless.onLogin();
            this.__loginSeamless.onLoginStart();
            if (result.status === Status.Failed) {
              this.__loginSeamless.onError();
              return nextLogin;
            } else if (result.status === Status.Success) {
              return () => this.__loginSeamless.onSuccess(result.response, result);
            }
            break;
          case 'wifi':
            const startLoginResult = this.startLogin(next);
            return startLoginResult;
        }
      } else {
        const startLoginResult = this.startLogin(next);
        return startLoginResult;
      }
    } catch (error) {
      this.__loginSeamless.onLoginStart();
      if (next) {
        return () => {
          this.__loginSeamless.onError();
          nextLogin();
        };
      } else {
        throw new SeamlessAmbiguityException(JSON.stringify(error, null, '\t'));
      }
    }
  };

  startLogin = async next => {
    if (next) {
      const nextLogin = _isEqual(next, this.__loginSoft)
        ? () => {
          this.softLogin();
        }
        : _isEqual(next, this.__savedAccounts) && (await this.hasSavedAccountList())
          ? () => {
            this.savedAccountsLogin();
          }
          : () => {
            this.upfrontLogin();
          };
      next.onLoginStart();
      return nextLogin;
    } else {
      throw new SeamlessAmbiguityException('No internet or wifi connection detected');
    }
  };

  onNext = (screen, otherParam = {}) => {
    const { navigateWithResetAction, navigate } = Navigation.instance;
    const { renderLoginIconView, renderVerifyLoginIconView } = this;
    const tobiLessConfig = {
      renderLoginIconView,
      renderVerifyLoginIconView,
    };
    return (param, config) => {
      const { resetNavigation } = config || {};
      const _navigate = !resetNavigation ? navigate : navigateWithResetAction;
      _navigate(screen, {
        ...tobiLessConfig,
        ...param,
        ...otherParam,
      });
    };
  };

  verifyCodeScreen = (onVerifyCodeSuccess, onCodeSubmit, phone) => {
    const {
      getSMSCode,
      verifyCodeLength,
      getResendCodeTimeout,
      withCountdown,
      countdownProps,
      renderTitle,
      errorConfig,
      keyboardType,
      codeInputTextStyle,
    } = this.__verifyCode;
    const withLabels = this.hasUpfrontLogin;
    const param = {
      onVerifyCodeSuccess,
      onCodeSubmit: (...args) => onCodeSubmit(...args, verifyCodeLength),
      phone,
      getSMSCode,
      verifyCodeLength,
      getResendCodeTimeout,
      withCountdown,
      countdownProps,
      renderTitle,
      withLabels,
      onSecondLabelPress: this.upfrontLogin,
      errorConfig,
      keyboardType,
      codeInputTextStyle,
    };
    this.onNext('VerifyCodeScreen')(param);
  };

  softLogin = config => {
    const hasUpfrontLogin = this.hasUpfrontLogin;
    const authConfig = this.__loginSoft.getAuthConfig();
    const { onVerifyCodeSuccess, onCodeSubmit } = this.__loginSoft;

    const param = {
      ...this.__loginSoft,
      authConfig,
      hasUpfrontLogin,
      onNext: phone => this.verifyCodeScreen(onVerifyCodeSuccess, onCodeSubmit, phone),
      onUpfrontLogin: this.upfrontLogin,
      getSMSCode: this.__verifyCode.getSMSCode,
    };
    this.onNext('PhoneLoginScreen')(param, config);
  };

  ResetPassword = blockedEmail => {
    const { resetPasswordConfig, validatePhone, validatePassword, renderTitle } =
      this.__loginUpfront;
    const { onCodeSubmit, onNewPasswordConfirmSuccess, onNewPasswordConfirm, maxLength } =
      resetPasswordConfig || {};
    const hasUpfrontLogin = this.hasUpfrontLogin;
    const authConfig = this.__loginUpfront.getAuthConfig();
    const onVerifyCodeSuccess = phone => {
      const blockedEmailOrPhoneNumber = blockedEmail ? blockedEmail : phone;
      const param = {
        validatePassword,
        onNewPasswordConfirm: newPassword =>
          onNewPasswordConfirm(blockedEmailOrPhoneNumber, newPassword, {
            phone,
            email: blockedEmail,
          }),
        onNewPasswordConfirmSuccess,
        renderTitle,
      };
      this.onNext('ConfirmNewPasswordScreen')(param);
    };
    const param = {
      authConfig,
      validatePhone,
      hasUpfrontLogin,
      maxLength,
      onNext: phone =>
        this.verifyCodeScreen(onVerifyCodeSuccess.bind(null, phone), onCodeSubmit, phone),
      onUpfrontLogin: this.upfrontLogin,
      getSMSCode: this.__verifyCode.getSMSCode,
      renderTitle,
      mode: 'forgotPassword',
      checkPhoneNumberStatus: this.__loginSoft.checkPhoneNumberStatus,
      onRegister: this.Register,
    };
    this.onNext('PhoneLoginScreen')(param);
  };

  upfrontLogin = async config => {
    const {
      onForgetPassword,
      onResetAccount,
      backBtnPress,
      getIsBiometricsLoginEnabled,
      keyboardType,
    } = this.__loginUpfront;

    const insideBackBtn =
      this.__loginUpfront.insideBackBtn ||
      (this.__savedAccounts && (await this.hasSavedAccountList()));
    const withBiometricsLogin = await getIsBiometricsLoginEnabled();

    const params = {
      ...this.__loginUpfront,
      withBiometricsLogin,
      authConfig: this.__loginUpfront.getAuthConfig(),
      onRegister: this.Register,
      onForgetPassword: onForgetPassword || this.ResetPassword,
      onResetAccount: onResetAccount || this.ResetPassword,
      insideBackBtn,
      backBtnPress: backBtnPress || this.savedAccountsLogin,
      keyboardType: keyboardType,
    };

    this.onNext('LoginByEmailScreen')(params, config);
  };

  Register = () => {
    // if there is no implementation for register use the one provided in upFrontlogin
    if (!this.__register) {
      this.__loginUpfront.onRegister();
      return;
    }
    const onRegister = this.__register.onRegister;
    const validatePhone = this.__register.validatePhone;
    const validatePassword = this.__register.validatePassword;
    const getSMSCode = this.__verifyCode.getSMSCode;
    const onResetAccount = this.__register.onResetAccount || this.ResetPassword;
    const { onVerifyCodeSuccess, onCodeSubmit } = this.__register;
    const param = {
      onRegister,
      getSMSCode,
      validatePhone,
      validatePassword,
      onResetAccount,
      onUpfrontLogin: this.upfrontLogin,
      onNext: phone => this.verifyCodeScreen(onVerifyCodeSuccess, onCodeSubmit, phone),
    };
    this.onNext('RegisterScreen')(param);
  };

  savedAccountsLogin = async config => {
    const savedAccounts = await this.__savedAccounts.getSavedAccountsList();
    const authConfig = this.__savedAccounts.getAuthConfig();
    const param = {
      onUpfrontLogin: this.upfrontLogin,
      savedAccounts,
      authConfig,
      onRegister: this.Register,
      ...this.__savedAccounts,
    };

    this.onNext('SavedAccountsScreen')(param, config);
  };

  checkInternetConnection = async () => {
    await axios.get('https://www.google.com/', {
      timeout: 1500,
    });
    return true;
  };
}

export default LoginManager;
